/* ----------------------------------------------------------------------------
 * VoiceLeaseTakeoverPill — the cross-client arbitration UI signal (residual #3).
 * Rendered by HandsFreeProvider ONLY for a non-holding, wake-eligible client
 * (leaseActive && gate==='other'): a compact pill telling the user that Aura is
 * listening on another device, with an explicit "Listen here" takeover control.
 * The wake engine is NOT armed on this device while this shows; PTT (the manual
 * Aura launcher) is unaffected and fully functional.
 * -------------------------------------------------------------------------- */
import { cn } from '@/lib/utils';

export interface VoiceLeaseTakeoverPillProps {
  /** the OTHER device's short label (e.g. "Chrome on Windows"), if known. */
  holderLabel: string | null;
  /** explicit user takeover — seizes the lease; the old holder yields shortly. */
  onListenHere: () => void;
  className?: string;
}

export function VoiceLeaseTakeoverPill({ holderLabel, onListenHere, className }: VoiceLeaseTakeoverPillProps) {
  const where = holderLabel && holderLabel !== '' ? ` (${holderLabel})` : '';
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed bottom-4 right-4 z-40 flex items-center gap-3 rounded-full',
        'border border-slate-200 bg-white/95 px-4 py-2 shadow-lg backdrop-blur',
        'dark:border-slate-700 dark:bg-slate-800/95',
        className,
      )}
    >
      <span className="text-sm text-slate-600 dark:text-slate-300">
        Aura is listening on another device{where}
      </span>
      <button
        type="button"
        onClick={onListenHere}
        className={cn(
          'rounded-full bg-slate-900 px-3 py-1 text-sm font-medium text-white',
          'hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400',
          'dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200',
        )}
      >
        Listen here
      </button>
    </div>
  );
}
